import React, {useMemo, useState} from "react";
import PropTypes from 'prop-types';
import {useAuth} from "../../providers/AuthProvider";
import LoadingBanner from "../../components/LoadingBanner";
import {Link, useLocation, useSearchParams} from "react-router-dom";
import ErrorBanner from "../../components/ErrorBanner";
import CandidateProvider from "../../providers/CandidateProvider";
import {usePosition} from "../../providers/ProjectPositionProvider";
import {useProject} from "../../providers/ClientProjectProvider";
import CardList from "../../components/CardList";
import PositionCostOverview from "./components/PositionCostOverview";
import ShortlistedCandidateList from "./components/ShortlistedCandidateList";
import InfoTooltip from "../../components/InfoTooltip";
import CandidateTile from "../talent/CandidateTile";
import PositionFormProvider from "../../providers/PositionFormProvider";
import ProjectFormProvider, {useProjectForm} from "../../providers/ProjectFormProvider";
import {Empty} from "antd";
import {useAPI} from "../../config/API";

const ViewOptions = {
    List: false,//'fa-list',
    Cards: 'fa-th-large'
}

const ShortlistSelectedCandidate = (props) => {

    const { position } = usePosition();

    const { auth } = useAuth();
    const { project, isLoading, formModel } = useProjectForm();

    if (isLoading || !formModel || !position) return null;

    return (
        <PositionFormProvider positionId={position.id}>
            <PositionCostOverview
                canEdit={false}
                isDisabled={props.isDisabled} />
        </PositionFormProvider>
    );
}
const PositionShortlistedCandidates = (props) => {
    const { position, isLoading: isPositionUpdating, displayIndex, deletePosition, updatePosition, reopenPosition, isDisabled } = usePosition();
    const { project, isProjectUpdating, unlinkAppSheetForProjectPosition } = useProject();
    const [searchParams, setSearchParams] = useSearchParams()
    const [activeView, setActiveView] = useState('List');
    const location = useLocation();

    let { onRequest } = props;

    const stats = useMemo(() => {
        let result = project.isFixedFee() ? {} : (position.isFixedRetainer() ? {
            [`EST. COST PER ${position.payout_frequency === 'weekly' ? 'WEEK' : 'MONTH'}`]: `${TB.formatCurrency(position.calculateFixedRetainerBudgetPerPeriod())}`,
        } : {
            'EST. HOURLY RATE': `${TB.formatCurrency(position.platform_rate)}/hr`,
        })
        if (project.isTimeMaterials()) {
            result['EST. HOURS PER WEEK'] = `${position.avg_hours_per_period} hours`;
        }
        if (project.isFixedFee()) {
            result['EST. TOTAL HOURS'] = `${position.estimated_total_periods} hours`;
        }

        return {
            ...result,
            [project.isFixedFee() ? 'TOTAL ORIGINAL ESTIMATED FEE' : 'TOTAL ESTIMATED FEE']: TB.formatCurrency(position.platform_cost),
            'START AND END DATE': position.timeRange()
        }
    }, [
        position?.assigned_candidate?.id,
        position.actual_total_periods,
        position.estimated_total_periods,
        position.payout_amount,
        position.platform_cost,
        position.platform_rate,
        position.total_selected_candidates,
        position.interested_candidates_count
    ]);
    const hasBeenFilled = position?.assigned_candidate?.id;
    const overrideShowShortlist = project.isActive();

    const { auth } = useAuth();

    const {
        data: candidateData,
        isFetching,
        isInitialLoading: isLoading,
        isRefetching,
        error,
        refetch
    } = useAPI(['positionMatches', {
        project_position: props.position.id,
        accomplished_disciplines: TB.map(props.position.disciplines, 'id'),
        shortlisted: 1,
        per_page: 500,
        capture_candidates_request: 1
    }], {
        enabled: !hasBeenFilled || overrideShowShortlist,
        select: data => {
            if (data?.users?.length) {
                let users = TB.orderBy(data.users, user => {
                    if (user.declinedPosition()) return 0;
                    if (user.isAssigned()) return 7;
                    if (user.statusIsApproved() && !user.isFixedFee()) return 6;
                    if (user.isFixedFee() && user.hasConfirmedPayout() && user.hasConfirmedScope()) return 5;
                    if (user.isFixedFee() && user.hasConfirmedScope()) return 4;
                    if (user.matchStatusIsInterested()) return 3;
                    if (user.statusIsPending()) return 2;

                    return 1;
                }, 'desc')

                return {
                    ...data, users
                }
            }
            return data;
        }
    }
    )
    const canAddRemoveProjectPositions = !project.isActive() && auth?.canAddRemoveProjectPositions(project);
    const canInviteTalentForProject = !project.isActive() && auth?.canInviteTalentForProject(project);
    const canShare = !position?.hasBeenFilled() || (auth.canManageClients() && project.isActive());

    const onShare = e => {
        e.currentTarget.blur();
        let params = new URLSearchParams(searchParams);
        params.set('shareTalentForRole', String(position?.id));
        return setSearchParams(params);
    }
    const canEdit = useMemo(() => {
        return project && auth?.isClientConsultant()
    }, [auth?.type, isLoading, project?.status]);

    // const [isPositionAppSheetOptionModalOpen, setIsPositionAppSheetOptionModalOpen] = useState(null)


    return (
        <div className="shortlisted-candidates-card container mx-auto max-w-[1225px]">

            <div className="role-header">

                <div className="flex flex-row items-center justify-start">
                    <h6>
                        Position {displayIndex} of {project?.sortedPositions()?.length}
                    </h6>

                    <p className={TB.mapClass("role-status", { filled: hasBeenFilled })}>
                        {hasBeenFilled ? 'POSITION FILLED' : 'POSITION NOT FILLED'}
                    </p>
                </div>
                <div className="flex flex-row items-center self-stretch flex-wrap gap-4">
                    <Link className="hover:opacity-50 transition duration-200" to={location.pathname.replace(/shortlist.*$/, `brief/role-requirements/role/${position.id}`)}>
                        <h3>{position.title}</h3>
                    </Link>

                    <div className="flex flex-row gap-6 ml-auto">

                        {/*TODO Enable App Sheet linking*/}
                        {/*{auth.canManageClients() && hasBeenFilled && (*/}
                        {/*    <div className="bg-white p-2 min-w-[200px] self-start">*/}

                        {/*        <div className="flex justify-between gap-6 items-center pb-3">*/}

                        {/*            <p className="text-base">*/}
                        {/*                App Sheet Candidate ID*/}
                        {/*            </p>*/}

                        {/*            <div className="flex gap-4 items-center">*/}
                        {/*                {position.appsheet_id && (*/}
                        {/*                    <div>*/}
                        {/*                        <button*/}
                        {/*                            disabled={isPositionUpdating}*/}
                        {/*                            id={`unlinkPosition-${position.id}`}*/}
                        {/*                            onClick={async () => {*/}
                        {/*                                try {*/}
                        {/*                                    await unlinkAppSheetForProjectPosition(position.id);*/}
                        {/*                                    await updatePosition({*/}
                        {/*                                        ...position,*/}
                        {/*                                        appsheet_id: null*/}
                        {/*                                    }, false);*/}
                        {/*                                } catch (error) {*/}
                        {/*                                    TB.alert(*/}
                        {/*                                        'error', 'Something went wrong',*/}
                        {/*                                        'Appsheet was unable to be unlinked at this time',*/}
                        {/*                                    );*/}
                        {/*                                }*/}
                        {/*                            }}*/}
                        {/*                        >*/}
                        {/*                            <i className="fas fa-unlink"/>*/}
                        {/*                        </button>*/}
                        {/*                        <InfoTooltip type="dark" target={`unlinkPosition-${position.id}`}>*/}
                        {/*                            Unlink App Sheet ID for Position*/}
                        {/*                        </InfoTooltip>*/}
                        {/*                    </div>*/}
                        {/*                )}*/}

                        {/*                <div>*/}
                        {/*                    <button*/}
                        {/*                        id={`changePosition-${position.id}`}*/}
                        {/*                        disabled={isPositionUpdating}*/}
                        {/*                        onClick={() => setIsPositionAppSheetOptionModalOpen(position.id)}*/}
                        {/*                    >*/}
                        {/*                        <i className="fas fa-sync"/>*/}
                        {/*                    </button>*/}
                        {/*                    <InfoTooltip type="dark" target={`changePosition-${position.id}`}>*/}
                        {/*                        Change App Sheet ID for Candidate*/}
                        {/*                    </InfoTooltip>*/}
                        {/*                </div>*/}
                        {/*            </div>*/}
                        {/*        </div>*/}

                        {/*        <p className="text-secondary font-semibold">*/}
                        {/*            {position.appsheet_id || 'Missing'}*/}
                        {/*        </p>*/}

                        {/*        <Modal*/}
                        {/*            okText="Submit"*/}
                        {/*            title={"Link Candidate ID to App Sheet"}*/}
                        {/*            closable={true}*/}
                        {/*            maskClosable={false}*/}
                        {/*            style={{top: 50}}*/}
                        {/*            destroyOnClose={true}*/}
                        {/*            width={700}*/}
                        {/*            footer={null}*/}
                        {/*            open={isPositionAppSheetOptionModalOpen === position.id}*/}
                        {/*            onCancel={() => setIsPositionAppSheetOptionModalOpen(false)}*/}
                        {/*            onClose={() => setIsPositionAppSheetOptionModalOpen(false)}*/}
                        {/*        >*/}
                        {/*            <PositionAppSheetOptionsModal*/}
                        {/*                onClose={() => setIsPositionAppSheetOptionModalOpen(false)}*/}
                        {/*            />*/}
                        {/*        </Modal>*/}

                        {/*    </div>*/}
                        {/*)}*/}

                        {!project.isActive() && !hasBeenFilled && canAddRemoveProjectPositions && (
                            <button onClick={() => onRequest(() => deletePosition())}
                                    disabled={isDisabled || props.isDisabled}
                                    className="btn btn-link-danger text-base self-center">Delete Position</button>
                        )}
                        {!project.isActive() && hasBeenFilled && canInviteTalentForProject && (
                            <button className="btn btn-outline-dark btn-outline-white self-center"
                                    onClick={() => onRequest(() => reopenPosition())}
                                    disabled={isDisabled || props.isDisabled}>
                                Reopen Position ({position.total_selected_candidates - 1})
                            </button>
                        )}

                        {canShare && (
                            <React.Fragment>
                                <button type="button" id="share-btn"
                                        disabled={isFetching || isDisabled || props.isDisabled}
                                        className="btn btn-primary" onClick={onShare}>
                                    <i className="fa fa-share mr-2 self-center"/>
                                    Share
                                </button>
                                <InfoTooltip type="dark" target="share-btn">
                                    Share talent profiles with other team members
                                </InfoTooltip>
                            </React.Fragment>
                        )}
                    </div>

                </div>
            </div>


            {(hasBeenFilled && !overrideShowShortlist) ? (
                <React.Fragment>

                    <ProjectFormProvider>
                        <ShortlistSelectedCandidate editable isDisabled={props.isDisabled}/>
                    </ProjectFormProvider>

                </React.Fragment>
            ) : (
                <div className={TB.mapClass("candidates-view", {
                    "empty": !isLoading && !error && candidateData && candidateData?.users?.length === 0
                })}>

                    <div className="candidate-stats">

                        <div className="flex flex-col gap-3 md:flex-row">
                            {TB.map(stats, (value, label) => (
                                <div key={label} className="flex-1 flex flex-col items-start justify-start">
                                    <p className="stat-label">
                                        {label}
                                    </p>
                                    <p className="stat-value">
                                        {value} {label === 'EST. HOURLY RATE' && <span className="ml-1">*</span>}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {((
                            position.isFixedFee() && position.fixedFeePricingStrategyIsFullBreakdown())
                            || (position.isFixedRetainer() && position.isFixedRetainerRateBased())
                            || position.isTimeMaterials()
                        ) && (
                            <p className="text-xs pt-2 text-light-gray6">
                                * Per average rate for role on Wripple
                            </p>
                        )}
                    </div>

                    <React.Fragment>
                        {(isLoading || (!candidateData && !error)) && (
                            <div className="flex flex-col items-stretch justify-center min-h-[100px]">
                                <LoadingBanner message="Finding Candidates"/>
                            </div>
                        )}

                        {!isLoading && !error && candidateData && (
                            candidateData?.users?.length > 0 ? (
                                <React.Fragment>
                                    <div className="flex flex-row items-center justify-start">
                                        <p className="mb-0 mr-2">
                                            View:
                                        </p>
                                        <div className="wripple-off-white-bg flex flex-row items-stretch">
                                            {TB.map(ViewOptions, (icon, label) => (
                                                <button
                                                    key={label}
                                                    disabled={isDisabled}
                                                    onClick={() => setActiveView(label)}
                                                    className={TB.mapClass("btn btn-toggle-view", {
                                                        "active": activeView === label
                                                    })}>
                                                    {icon && <i className={`fas ${icon}`} />}
                                                    <span>{label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {activeView === 'Cards' ? (
                                        <div className="candidates-list card-display">
                                            <CardList
                                                fillContainer={true}
                                                minHeight={715} itemsPerRow={3}
                                                items={TB.orderBy(candidateData?.users, 'is_assigned', 'desc')
                                                    .map((candidate, i) => {
                                                        if (!candidate.isShortlisted()) {
                                                            return null;
                                                        }
                                                        return (
                                                            <CandidateProvider
                                                                key={`${props.position.id}-talent-${candidate.id}-${props.position.id}`}
                                                                candidate={candidate}>
                                                                <CandidateTile
                                                                    shortlistView={true}
                                                                    candidate={candidate}
                                                                    onRequest={onRequest}
                                                                    isDisabled={props.isDisabled || isDisabled} />
                                                            </CandidateProvider>
                                                        );
                                                    })}
                                            />
                                        </div>
                                    ) : (
                                        <ShortlistedCandidateList
                                            candidates={candidateData?.users}
                                            onRequest={onRequest}
                                            isDisabled={isDisabled || props.isDisabled} />
                                    )}

                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <Empty className="bg-white py-5 m-0" image={Empty.PRESENTED_IMAGE_SIMPLE} description={(
                                        <p className="font-secondary mb-0 text-center text-[14px] leading-[160%]">
                                            No shortlist talent selected</p>
                                    )}>


                                        <Link className="self-center btn btn-primary w-[150px] mx-auto"
                                            to={location.pathname.replace(/shortlist.*$/, `talent-pool/role/${position.id}`)}>
                                            <i className="fas m-t-[-2px] fa-plus mr-2" />
                                            Add Talent
                                        </Link>
                                    </Empty>
                                </React.Fragment>
                            )
                        )}

                        {!isLoading && error && (
                            <div className="flex flex-col items-stretch justify-start min-h-[100px]">
                                <ErrorBanner
                                    title="Something went wrong"
                                    message="Unable to load candidates. Please try again later"
                                    onRetry={refetch} />
                            </div>
                        )}
                    </React.Fragment>
                </div>
            )}

        </div>
    )
}

PositionShortlistedCandidates.propTypes = {
    isDisabled: PropTypes.bool,
    candidates: PropTypes.array,
    linkParams: PropTypes.any,
    position: PropTypes.object,
    onUpdated: PropTypes.func,
    onUpdating: PropTypes.func,
    onAssignCandidate: PropTypes.func,
    onDelete: PropTypes.func,
    onViewProposal: PropTypes.func,
    canEdit: PropTypes.bool
};
export default PositionShortlistedCandidates